import React, { useState, useEffect } from 'react';
import { 
  Upload, 
  FileText, 
  Users, 
  Calendar,
  Download,
  Eye,
  Trash2,
  Plus,
  Filter,
  Search,
  X,
  Loader2,
  Cloud,
  UploadCloud,
  AlertTriangle,
  Lock
} from 'lucide-react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import DatabaseService from '../../services/databaseService';
import StorageService from '../../services/storageService';
import ErrorBoundary from '../ErrorBoundary';
import SubscriptionPopup from './SubscriptionPopup';
import { useAuth } from '../../contexts/AuthContext';

const SupervisorReports = ({ onUpdate, selectedClinic: superAdminSelectedClinic }) => {
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [supervisors, setSupervisors] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedClinic, setSelectedClinic] = useState('');
  const [selectedSupervisor, setSelectedSupervisor] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [showSubscriptionPopup, setShowSubscriptionPopup] = useState(false);
  const [showResponseUploadModal, setShowResponseUploadModal] = useState(false);
  const [selectedReportForResponse, setSelectedReportForResponse] = useState(null);
  const [clinicUsage, setClinicUsage] = useState({});
  const [subscriptions, setSubscriptions] = useState({});
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedReportForView, setSelectedReportForView] = useState(null);

  const { register, handleSubmit, reset, watch, formState: { errors } } = useForm({
    defaultValues: {
      clinicId: '',
      supervisorId: '',
      title: '',
      reportType: 'PID',
      notes: ''
    }
  });

  const watchedClinic = watch('clinicId');

  // Check if clinic has reached report limit
  const checkReportLimit = (clinicId) => {
    try {
      // Super Admin has no restrictions
      if (user?.role === 'super_admin') {
        return false;
      }
      
      const subscription = subscriptions[clinicId];
      const usage = clinicUsage[clinicId] || 0;
      
      if (subscription && subscription.status === 'active') {
        // Paid subscription - check against plan limit
        return usage >= (subscription.reportsAllowed || 0);
      } else {
        // Trial subscription - 10 report limit
        return usage >= 10;
      }
    } catch (error) {
      console.error('ERROR: Error checking report limit:', error);
      return false; // Default to not limiting if there's an error
    }
  };

  // Get clinic's current usage info
  const getClinicUsageInfo = (clinicId) => {
    try {
      // Super Admin has unlimited access
      if (user?.role === 'super_admin') {
        return {
          used: clinicUsage[clinicId] || 0,
          allowed: 'Unlimited',
          remaining: 'Unlimited',
          isTrial: false,
          planName: 'Super Admin - Unlimited Access'
        };
      }
      
      const subscription = subscriptions[clinicId];
      const usage = clinicUsage[clinicId] || 0;
      
      if (subscription && subscription.status === 'active') {
        return {
          used: usage,
          allowed: subscription.reportsAllowed || 0,
          remaining: Math.max(0, (subscription.reportsAllowed || 0) - usage),
          isTrial: false,
          planName: subscription.planName || 'Paid Plan'
        };
      } else {
        return {
          used: usage,
          allowed: 10,
          remaining: Math.max(0, 10 - usage),
          isTrial: true,
          planName: 'Trial Plan'
        };
      }
    } catch (error) {
      console.error('ERROR: Error getting clinic usage info:', error);
      return {
        used: 0,
        allowed: 10,
        remaining: 10,
        isTrial: true,
        planName: 'Trial Plan'
      };
    }
  };

  useEffect(() => {
    loadData();
  }, [superAdminSelectedClinic]);

  useEffect(() => {
    // Load patients when clinic changes in the form
    if (watchedClinic) {
      const loadClinicPatients = async () => {
        try {
          const clinicSupervisors = await DatabaseService.getSupervisorsByClinic(watchedClinic);
          setSupervisors(clinicSupervisors || []);
        } catch (error) {
          console.error('ERROR: Error loading clinic supervisors:', error);
          setSupervisors([]);
        }
      };
      loadClinicPatients();
    } else {
      setSupervisors([]);
    }
  }, [watchedClinic]);

  const loadData = async () => {
    try {
      setError(null); // Clear any previous errors
      setLoading(true);

      console.log('SUPERADMIN: SuperAdmin loading ALL supervisor reports from Supabase...');

      // SuperAdmin can see ALL data from all clinics, or filter by selected clinic
      // Ensure all database calls return arrays even if they fail
      let reportsData = [];
      let clinicsData = [];
      let patientsData = [];
      let subscriptionsData = [];

      try {
        reportsData = await DatabaseService.get('reports') || [];
      } catch (err) {
        console.error('ERROR: Failed to load reports:', err);
        reportsData = [];
      }

      try {
        clinicsData = await DatabaseService.get('clinics') || [];
      } catch (err) {
        console.error('ERROR: Failed to load project areas:', err);
        clinicsData = [];
      }

      try {
        patientsData = await DatabaseService.get('patients') || [];
      } catch (err) {
        console.error('ERROR: Failed to load supervisors:', err);
        patientsData = [];
      }

      try {
        subscriptionsData = await DatabaseService.get('subscriptions') || [];
      } catch (err) {
        console.error('ERROR: Failed to load subscriptions:', err);
        subscriptionsData = [];
      }

      // Ensure all data is actually arrays
      reportsData = Array.isArray(reportsData) ? reportsData : [];
      clinicsData = Array.isArray(clinicsData) ? clinicsData : [];
      patientsData = Array.isArray(patientsData) ? patientsData : [];
      subscriptionsData = Array.isArray(subscriptionsData) ? subscriptionsData : [];

      console.log('DATA: Fetched from Supabase:', {
        reports: reportsData.length,
        clinics: clinicsData.length,
        patients: patientsData.length,
        subscriptions: subscriptionsData.length
      });

      // Filter reports by selected clinic if specified (NO localStorage!)
      const filteredReportsData = superAdminSelectedClinic && reportsData.length > 0
        ? reportsData.filter(report => report && report.clinicId === superAdminSelectedClinic)
        : reportsData;

      // First, fix supervisor names in the reports
      const reportsWithFixedNames = fixPatientNames(filteredReportsData, patientsData);

      // Validate and fix report data
      const validatedReports = validateReportData(reportsWithFixedNames);

      // Enhance reports with clinic and supervisor names, and extract file paths
      const enhancedReports = validatedReports.map(report => {
        const clinic = clinicsData.find(c => c && c.id === report.clinicId);

        // Extract file path and metadata from reportData if they exist
        const reportData = report.reportData || report.report_data || {};

        return {
          ...report,
          clinicName: clinic?.name || 'Unknown Project Area',
          patientName: report.patientName || 'Unknown Supervisor',
          // Ensure file path fields are available at top level for easy access
          storagePath: report.storagePath || report.filePath || report.file_path || reportData.storagePath || reportData.filePath,
          s3Key: report.s3Key || reportData.s3Key,
          fileUrl: report.fileUrl || reportData.fileUrl,
          storedInCloud: report.storedInCloud || reportData.storedInCloud || false,
          title: report.title || reportData.title || report.fileName
        };
      }).sort((a, b) => new Date(b.uploadedAt || b.createdAt || 0) - new Date(a.uploadedAt || a.createdAt || 0));

      setReports(enhancedReports);
      setClinics(clinicsData);

      // Filter patients by selected clinic if specified
      console.log('DEBUG: Before filtering supervisors:', {
        totalSupervisors: patientsData.length,
        superAdminSelectedClinic: superAdminSelectedClinic,
        sampleSupervisor: patientsData[0]
      });

      const filteredSupervisorsData = superAdminSelectedClinic && patientsData.length > 0
        ? patientsData.filter(supervisor => supervisor && supervisor.clinicId === superAdminSelectedClinic)
        : patientsData;

      console.log('DEBUG: After filtering supervisors:', {
        filteredCount: filteredSupervisorsData.length,
        supervisors: filteredSupervisorsData.map(s => ({ id: s.id, name: s.name, clinicId: s.clinicId }))
      });

      setSupervisors(filteredSupervisorsData);

      // Calculate clinic usage and load subscriptions (using Supabase data only)
      const usageMap = {};
      const subscriptionMap = {};

      clinicsData.forEach(clinic => {
        if (clinic && clinic.id) {
          const clinicReports = reportsData.filter(report => report && report.clinicId === clinic.id);
          usageMap[clinic.id] = clinicReports.length;
        }
      });

      subscriptionsData.forEach(subscription => {
        if (subscription && subscription.clinicId) {
          subscriptionMap[subscription.clinicId] = subscription;
        }
      });

      setClinicUsage(usageMap);
      setSubscriptions(subscriptionMap);
    } catch (error) {
      console.error('ERROR: Critical error loading admin supervisor reports:', error);
      setError(`Failed to load data: ${error.message || 'Unknown error'}`);
      toast.error('Error loading supervisor reports data');
      // Set default empty states to prevent rendering errors
      setReports([]);
      setClinics([]);
      setSupervisors([]);
      setClinicUsage({});
      setSubscriptions({});
    } finally {
      setLoading(false);
    }
  };

  // Function to fix supervisor names in reports
  const fixPatientNames = (reports, supervisors) => {
    // Safety check: ensure inputs are arrays
    if (!Array.isArray(reports)) return [];
    if (!Array.isArray(supervisors)) supervisors = [];

    return reports.map(report => {
      // Safety check: ensure report is an object
      if (!report || typeof report !== 'object') {
        return { patientName: 'Unknown Supervisor' };
      }

      // If report already has a supervisor name, use it
      if (report.patientName && report.patientName !== 'Unknown Supervisor') {
        return report;
      }

      // Try to find supervisor by ID with multiple field variations
      let supervisor = supervisors.find(p =>
        p && (p.id === report.patientId || p.id === report.patient_id)
      );

      // Get supervisor name with multiple field variations
      const getPatientName = (p) => {
        if (!p || typeof p !== 'object') return null;
        return p.name || p.fullName || p.full_name || null;
      };

      // If not found by ID, try to find by name (case insensitive)
      if (!supervisor && report.patientName) {
        supervisor = supervisors.find(p => {
          const pName = getPatientName(p);
          return pName && pName.toLowerCase() === report.patientName.toLowerCase();
        });
      }

      // If still not found, try partial name matching
      if (!supervisor && report.patientName) {
        supervisor = supervisors.find(p => {
          const pName = getPatientName(p);
          return pName && pName.toLowerCase().includes(report.patientName.toLowerCase());
        });
      }

      const patientName = supervisor ? getPatientName(supervisor) : (report.patientName || 'Unknown Supervisor');

      return {
        ...report,
        patientName: patientName
      };
    });
  };

  // Function to validate and fix report data
  const validateReportData = (reports) => {
    // Safety check: ensure input is an array
    if (!Array.isArray(reports)) return [];

    return reports.map(report => {
      // Safety check: ensure report is an object
      if (!report || typeof report !== 'object') {
        return { fileName: 'Unknown_Report.pdf', fileType: 'application/pdf', storedInCloud: false };
      }

      const issues = [];

      // Check for missing file information
      if (!report.fileName) {
        issues.push('Missing fileName');
        report.fileName = 'Unknown_Report.pdf';
      }

      // Check for missing S3 key but has storedInCloud flag
      if (report.storedInCloud && !report.s3Key) {
        issues.push('Missing S3 key but marked as stored in cloud');
        report.storedInCloud = false;
      }

      // Check for invalid file types
      if (report.fileType && !['application/pdf', 'image/jpeg', 'image/png', 'application/octet-stream'].includes(report.fileType)) {
        issues.push(`Invalid file type: ${report.fileType}`);
        report.fileType = 'application/pdf';
      }

      if (issues.length > 0) {
        // Report data issues found - silently fix them
      }

      return report;
    });
  };

  const handleFileSelect = (event) => {
    // Safety check: ensure event and target exist
    if (!event || !event.target || !event.target.files) {
      console.error('ERROR: Invalid file selection event');
      return;
    }

    const file = event.target.files[0];
    if (file) {
      try {
        // Validate file
        StorageService.validateFile(file);
        setSelectedFile(file);
        console.log('SUCCESS: File selected:', file.name);
      } catch (error) {
        console.error('ERROR: File validation failed:', error);
        toast.error(error.message || 'Invalid file selected');
        setSelectedFile(null);
      }
    }
  };

  const handleUploadReport = async (data) => {
    if (!selectedFile) {
      toast.error('Please select a file to upload');
      return;
    }

    // Validate required fields
    if (!data.clinicId) {
      toast.error('Please select a project area');
      return;
    }

    if (!data.supervisorId) {
      toast.error('Please select a supervisor');
      return;
    }

    if (!data.title) {
      toast.error('Please enter a report title');
      return;
    }

    // Super Admin can upload reports without restrictions
    if (user?.role === 'super_admin') {
      // Super Admin has unlimited access
    } else {
      // For regular users, check if clinic has reached report limit
      if (checkReportLimit(data.clinicId)) {
        setShowSubscriptionPopup(true);
        toast.error('Report limit reached. Please upgrade your plan to continue uploading reports.');
        return;
      }
    }

    setUploadingFile(true);
    setUploadProgress(0);

    try {
      console.log('START: Starting file upload to S3...', {
        file: selectedFile.name,
        clinicId: data.clinicId,
        supervisorId: data.supervisorId
      });
      
      // Validate file again before upload
      StorageService.validateFile(selectedFile);
      
      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 200);

      // Upload file to S3
      const uploadResult = await StorageService.uploadFile(
        selectedFile,
        selectedFile.name,
        {
          clinicId: data.clinicId,
          patientId: data.supervisorId, // Database still uses patientId column
          reportType: data.reportType || 'PID',
          uploadedBy: 'Super Admin'
        }
      );

      clearInterval(progressInterval);
      setUploadProgress(100);

      console.log('SUCCESS: File uploaded to S3:', uploadResult);

      // Save report metadata to database
      // Actual Supabase reports table schema (from 004_simple_clinic_tables.sql):
      // - clinic_id, patient_id, file_name, file_path, report_data (JSONB), status
      const reportData = {
        clinicId: data.clinicId,
        patientId: data.supervisorId, // Database still uses patientId column
        fileName: selectedFile.name,
        filePath: uploadResult.path || uploadResult.key,
        reportData: {
          // Store ALL metadata in JSONB field
          title: data.title || selectedFile.name,
          reportType: data.reportType || 'Standard',
          description: data.notes || '',
          fileSize: selectedFile.size,
          fileType: selectedFile.type,
          s3Key: uploadResult.key,
          s3Bucket: uploadResult.bucket,
          s3Region: uploadResult.region,
          s3FileName: uploadResult.fileName,
          s3UploadedAt: uploadResult.uploadedAt,
          s3ETag: uploadResult.etag,
          uploadedBy: 'Super Admin',
          uploadedByUserId: user?.id,
          uploadStatus: 'completed',
          storedInCloud: true
        },
        status: 'completed'
      };
      
      const savedReport = await DatabaseService.addReport(reportData);
      
      if (!savedReport) {
        throw new Error('Failed to save report to database');
      }
      
      console.log('SUCCESS: Report saved successfully:', savedReport.id);
      toast.success(` Report uploaded successfully to Cloud Storage!`);
      loadData();
      setShowUploadModal(false);
      setSelectedFile(null);
      setUploadProgress(0);
      reset();
      onUpdate?.();
    } catch (error) {
      console.error('ERROR: Admin Upload Error:', error);
      console.error('Error context:', {
        message: error.message,
        formData: data,
        file: selectedFile ? {
          name: selectedFile.name,
          size: selectedFile.size,
          type: selectedFile.type
        } : null
      });
      
      const errorMessage = error.message || 'Unknown error occurred';
      toast.error(`Upload failed: ${errorMessage}`);
      setUploadProgress(0);
    } finally {
      setUploadingFile(false);
    }
  };

  const handleDeleteReport = async (reportId) => {
    if (window.confirm('Are you sure you want to delete this report? This will also remove the file from Cloud Storage.')) {
      try {
        // Get report data to find S3 key
        const report = reports.find(r => r.id === reportId);
        
        if (report && report.s3Key) {
          console.log('DELETE: Deleting file from S3:', report.s3Key);
          try {
            await StorageService.deleteFile(report.storagePath || report.s3Key);
            console.log('SUCCESS: File deleted from S3');
          } catch (s3Error) {
            console.warn('WARNING: Could not delete file from S3:', s3Error.message);
            // Continue with database deletion even if S3 deletion fails
          }
        }

        // Delete from database
        DatabaseService.delete('reports', reportId);
        toast.success('Report deleted successfully from database and S3');
        loadData();
        onUpdate?.();
      } catch (error) {
        toast.error('Error deleting report');
        console.error(error);
      }
    }
  };

  const handleSubscription = async (subscriptionData) => {
    try {
      // Save subscription to database
      await DatabaseService.add('subscriptions', subscriptionData);
      
      // Update clinic's subscription status
      await DatabaseService.update('clinics', subscriptionData.clinicId, {
        subscriptionStatus: 'active',
        reportsAllowed: subscriptionData.reportsAllowed
      });
      
      // Reload data to reflect changes
      loadData();
      
      toast.success('Subscription updated successfully!');
    } catch (error) {
      console.error('Error updating subscription:', error);
      toast.error('Failed to update subscription');
    }
  };

  const handleUploadResponse = (report) => {
    console.log('NOTE: Opening response upload modal for report:', report.id);
    setSelectedReportForResponse(report);
    setShowResponseUploadModal(true);
  };

  const handleSubmitResponse = async (responseData) => {
    if (!selectedFile || !selectedReportForResponse) {
      toast.error('Please select a response file to upload');
      return;
    }

    setUploadingFile(true);
    setUploadProgress(0);

    try {
      console.log('START: Uploading response report to S3...', {
        originalReport: selectedReportForResponse.id,
        responseFile: selectedFile.name,
        clinicId: selectedReportForResponse.clinicId
      });
      
      // Validate file
      StorageService.validateFile(selectedFile);
      
      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 200);

      // Upload response file to S3
      const uploadResult = await StorageService.uploadFile(
        selectedFile, 
        `response_${selectedFile.name}`,
        {
          clinicId: selectedReportForResponse.clinicId,
          patientId: selectedReportForResponse.patientId,
          reportType: 'Response Report',
          uploadedBy: 'Super Admin',
          originalReportId: selectedReportForResponse.id
        }
      );

      clearInterval(progressInterval);
      setUploadProgress(100);

      console.log('SUCCESS: Response file uploaded to S3:', uploadResult);

      // Save response report metadata to database
      // Actual Supabase reports table schema (from 004_simple_clinic_tables.sql):
      // - clinic_id, patient_id, file_name, file_path, report_data (JSONB), status
      const responseReportData = {
        clinicId: selectedReportForResponse.clinicId,
        patientId: selectedReportForResponse.patientId,
        fileName: selectedFile.name,
        filePath: uploadResult.path || uploadResult.key,
        reportData: {
          // Store ALL metadata in JSONB field
          title: `Response to: ${selectedReportForResponse.title || selectedReportForResponse.fileName}`,
          reportType: 'Response Report',
          description: responseData?.notes || 'Response report uploaded by Super Admin',
          fileSize: selectedFile.size,
          fileType: selectedFile.type,
          s3Key: uploadResult.key,
          s3Bucket: uploadResult.bucket,
          s3Region: uploadResult.region,
          s3FileName: uploadResult.fileName,
          s3UploadedAt: uploadResult.uploadedAt,
          s3ETag: uploadResult.etag,
          originalReportId: selectedReportForResponse.id,
          isResponseReport: true,
          uploadedBy: 'Super Admin',
          uploadedByUserId: user?.id,
          uploadStatus: 'completed',
          storedInCloud: true
        },
        status: 'completed'
      };
      
      const savedResponse = await DatabaseService.addReport(responseReportData);
      
      if (!savedResponse) {
        throw new Error('Failed to save response report to database');
      }
      
      console.log('SUCCESS: Response report saved successfully:', savedResponse.id);
      toast.success(`NOTE: Response report uploaded successfully for ${selectedReportForResponse.patientName}!`);
      
      // Reload data and close modal
      loadData();
      setShowResponseUploadModal(false);
      setSelectedReportForResponse(null);
      setSelectedFile(null);
      setUploadProgress(0);
      
      onUpdate?.();
    } catch (error) {
      console.error('ERROR: Response Upload Error:', error);
      const errorMessage = error.message || 'Unknown error occurred';
      toast.error(`Response upload failed: ${errorMessage}`);
      setUploadProgress(0);
    } finally {
      setUploadingFile(false);
    }
  };

  const handleViewReport = (report) => {
    console.log(' Super Admin viewing report:', report.fileName);
    setSelectedReportForView(report);
    setShowViewModal(true);
  };

  const handleDownloadReport = async (report) => {
    try {
      // For regular users (not super_admin), check if clinic has reached report limit
      if (user?.role !== 'super_admin' && checkReportLimit(report.clinicId)) {
        setShowSubscriptionPopup(true);
        toast.error('Report limit reached. Please upgrade your plan to continue downloading reports.');
        return;
      }

      let downloadUrl = null;
      let fileName = report.fileName || 'report.pdf';

      console.log('INFO: Downloading report:', {
        fileName,
        reportId: report.id,
        s3Key: report.s3Key,
        storagePath: report.storagePath,
        filePath: report.filePath || report.file_path,
        fileUrl: report.fileUrl,
        storedInCloud: report.storedInCloud,
        reportData: report.reportData
      });

      // Extract file path from multiple possible locations
      const possiblePaths = [
        report.storagePath,
        report.s3Key,
        report.filePath,
        report.file_path,
        report.reportData?.s3Key,
        report.reportData?.storagePath,
        report.reportData?.filePath
      ].filter(Boolean);

      console.log('DEBUG: Possible file paths:', possiblePaths);

      // Try multiple download methods
      let lastError = null;

      // Method 1: Try each possible path with Supabase Storage
      for (const path of possiblePaths) {
        try {
          console.log('DEBUG: Trying path:', path);
          downloadUrl = await StorageService.getSignedUrl(path, 300); // 5 minutes
          if (downloadUrl) {
            console.log('SUCCESS: Got signed URL from Supabase Storage using path:', path);
            break;
          }
        } catch (pathError) {
          console.warn('WARNING: Failed to get signed URL for path:', path, pathError.message);
          lastError = pathError;
        }
      }

      // Method 2: Fallback to direct file URL if available
      if (!downloadUrl && report.fileUrl) {
        downloadUrl = report.fileUrl;
        console.log('INFO: Using direct file URL');
      }

      // Method 3: Check if file URL is in reportData
      if (!downloadUrl && report.reportData?.fileUrl) {
        downloadUrl = report.reportData.fileUrl;
        console.log('INFO: Using file URL from reportData');
      }

      // Download file if URL is available
      if (downloadUrl) {
        // Create download link
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = fileName;
        link.target = '_blank';
        link.style.display = 'none';

        // Add to DOM, click, and remove
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Clean up blob URL if created
        if (downloadUrl.startsWith('blob:')) {
          setTimeout(() => URL.revokeObjectURL(downloadUrl), 1000);
        }

        toast.success(`Downloading ${fileName}`);
        console.log('SUCCESS: Download initiated for:', fileName);
      } else {
        // No download method available
        console.error('ERROR: No download URL available. Tried paths:', possiblePaths);
        console.error('ERROR: Last error:', lastError?.message);
        console.error('ERROR: Full report object:', report);
        toast.error(`Cannot download ${fileName}. File not found in storage.`);
      }
    } catch (error) {
      console.error('ERROR: Error downloading report:', error);
      toast.error(`Download failed: ${error.message}`);
    }
  };

  // Filter reports to EXCLUDE response reports (they will be shown in separate section)
  const filteredReports = (reports || []).filter(report => {
    // EXCLUDE response reports from main list
    const isResponseReport = report.reportData?.isResponseReport ||
                             report.report_data?.isResponseReport ||
                             report.reportData?.report_type === 'Response Report' ||
                             report.fileName?.startsWith('response_');

    if (isResponseReport) {
      return false; // Don't show response reports in main list
    }

    const matchesSearch = (report?.patientName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (report?.clinicName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (report?.fileName || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesClinic = !selectedClinic || report?.clinicId === selectedClinic;
    const matchesPatient = !selectedSupervisor || report?.patientId === selectedSupervisor;

    return matchesSearch && matchesClinic && matchesPatient;
  });

  // Get ONLY response reports for separate display
  const responseReports = (reports || []).filter(report => {
    const isResponseReport = report.reportData?.isResponseReport ||
                             report.report_data?.isResponseReport ||
                             report.reportData?.report_type === 'Response Report' ||
                             report.fileName?.startsWith('response_');

    if (!isResponseReport) {
      return false;
    }

    const matchesSearch = (report?.patientName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (report?.clinicName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (report?.fileName || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesClinic = !selectedClinic || report?.clinicId === selectedClinic;
    const matchesPatient = !selectedSupervisor || report?.patientId === selectedSupervisor;

    return matchesSearch && matchesClinic && matchesPatient;
  });

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center max-w-md">
          <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Failed to Load Supervisor Reports</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <div className="space-x-4">
            <button
              onClick={() => {
                setError(null);
                loadData();
              }}
              className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700"
            >
              Try Again
            </button>
            <button
              onClick={() => window.location.reload()}
              className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300"
            >
              Reload Page
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8">
      {/* Action Buttons */}
      <div className="flex items-center justify-end gap-3 mb-6">
        <button
          onClick={() => {
            loadData();
            toast.success('Supervisor reports refreshed!');
          }}
          className="flex items-center gap-2 bg-white hover:bg-gray-50 text-gray-700 px-4 py-2.5 rounded-lg font-medium text-sm border border-gray-200 transition-colors shadow-sm"
        >
          <Loader2 className="h-4 w-4" />
          <span>Refresh</span>
        </button>
        <button
          onClick={() => setShowUploadModal(true)}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-lg font-medium text-sm transition-colors shadow-sm"
        >
          <Upload className="h-4 w-4" />
          <span>Upload Report</span>
        </button>
      </div>

      {/* Usage Summary */}
      {superAdminSelectedClinic && (
        <div className={`rounded-lg shadow-sm border p-4 mb-4 ${
          user?.role === 'super_admin' 
            ? 'bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-200' 
            : 'bg-gradient-to-r from-[#E4EFFF] to-indigo-50 border-blue-200'
        }`}>
          <div className="flex items-center justify-between">
            <div>
              <h3 className={`text-lg font-semibold ${
                user?.role === 'super_admin' ? 'text-purple-900' : 'text-blue-900'
              }`}>
                {user?.role === 'super_admin' ? 'Super Admin - Clinic Overview' : 'Clinic Usage Summary'}
              </h3>
              {(() => {
                const usageInfo = getClinicUsageInfo(superAdminSelectedClinic);
                return (
                  <div className="mt-2 space-y-1">
                    <p className={user?.role === 'super_admin' ? 'text-purple-700' : 'text-blue-700'}>
                      <strong>Plan:</strong> {usageInfo.planName}
                    </p>
                    <p className={user?.role === 'super_admin' ? 'text-purple-700' : 'text-blue-700'}>
                      <strong>Usage:</strong> {usageInfo.used}/{usageInfo.allowed} reports
                    </p>
                    <p className={user?.role === 'super_admin' ? 'text-purple-700' : 'text-blue-700'}>
                      <strong>Remaining:</strong> {usageInfo.remaining} reports
                    </p>
                    {user?.role !== 'super_admin' && usageInfo.remaining <= 2 && (
                      <div className="flex items-center space-x-2 mt-2">
                        <AlertTriangle className="h-4 w-4 text-orange-500" />
                        <span className="text-orange-700 text-sm font-medium">
                          {usageInfo.remaining === 0 ? 'Report limit reached!' : 'Approaching report limit'}
                        </span>
                      </div>
                    )}
                    {user?.role === 'super_admin' && (
                      <div className="flex items-center space-x-2 mt-2">
                        <Lock className="h-4 w-4 text-[#323956]" />
                        <span className="text-green-700 text-sm font-medium">
                          Super Admin - Unlimited Access
                        </span>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
            <div className="text-right">
              {(() => {
                const usageInfo = getClinicUsageInfo(superAdminSelectedClinic);
                const percentage = user?.role === 'super_admin' ? 0 : (usageInfo.used / (usageInfo.allowed || 1)) * 100;
                return (
                  <div>
                    <div className={`text-2xl font-bold ${
                      user?.role === 'super_admin' ? 'text-purple-900' : 'text-blue-900'
                    }`}>{usageInfo.used}</div>
                    <div className={`text-sm ${
                      user?.role === 'super_admin' ? 'text-purple-700' : 'text-blue-700'
                    }`}>Reports Used</div>
                    {user?.role !== 'super_admin' && (
                      <div className="w-24 h-2 bg-blue-200 rounded-full mt-2">
                        <div 
                          className={`h-2 rounded-full ${
                            percentage >= 90 ? 'bg-red-500' : 
                            percentage >= 75 ? 'bg-orange-500' : 'bg-[#323956]'
                          }`}
                          style={{ width: `${Math.min(percentage, 100)}%` }}
                        ></div>
                      </div>
                    )}
                    {user?.role === 'super_admin' && (
                      <div className="w-24 h-2 bg-green-200 rounded-full mt-2">
                        <div className="h-2 rounded-full bg-[#323956]" style={{ width: '100%' }}></div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Search reports..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          
          <select
            value={selectedClinic}
            onChange={(e) => setSelectedClinic(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">All Project Areas</option>
            {(clinics || []).map(clinic => (
              <option key={clinic?.id || Math.random()} value={clinic?.id || ''}>{clinic?.name || 'Unknown Project Area'}</option>
            ))}
          </select>
          
          <select
            value={selectedSupervisor}
            onChange={(e) => setSelectedSupervisor(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            onClick={() => {
              console.log('DEBUG: Supervisor dropdown clicked:', {
                totalSupervisorsInState: supervisors?.length || 0,
                selectedClinic: selectedClinic,
                superAdminSelectedClinic: superAdminSelectedClinic,
                supervisorsData: supervisors?.map(s => ({ id: s.id, name: s.name, clinicId: s.clinicId }))
              });
            }}
          >
            <option value="">All Supervisors</option>
            {(() => {
              const clinicFilter = selectedClinic || superAdminSelectedClinic;
              const filtered = (supervisors || []).filter(supervisor => {
                return !clinicFilter || supervisor?.clinicId === clinicFilter;
              });

              console.log('DEBUG: Dropdown rendering:', {
                supervisorsTotal: supervisors?.length || 0,
                clinicFilter: clinicFilter,
                filteredCount: filtered.length,
                filtered: filtered.map(s => ({ id: s.id, name: s.name, clinicId: s.clinicId }))
              });

              return filtered.map(supervisor => (
                <option key={supervisor?.id || Math.random()} value={supervisor?.id || ''}>
                  {supervisor?.name || supervisor?.fullName || supervisor?.full_name || 'Unknown Supervisor'}
                </option>
              ));
            })()}
          </select>

          <button
            onClick={() => {
              setSearchTerm('');
              setSelectedClinic('');
              setSelectedSupervisor('');
            }}
            className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 flex items-center justify-center space-x-2"
          >
            <X className="h-4 w-4" />
            <span>Clear</span>
          </button>
        </div>
      </div>

      {/* Reports Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            Reports ({filteredReports.length})
          </h3>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Report
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Patient
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Clinic
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  File Info
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Uploaded
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {(filteredReports || []).map((report) => (
                <tr key={report.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="h-10 w-10 rounded-lg bg-[#CAE0FF] dark:bg-blue-900/30 flex items-center justify-center">
                        <FileText className="h-5 w-5 text-[#323956] dark:text-blue-400" />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">{report.fileName}</div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">{report.title || 'EEG Report'}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-white">{report.patientName}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-white">{report.clinicName}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-white flex items-center">
                      {report.fileType || 'PDF'}
                      {report.storedInCloud && (
                        <Cloud className="h-4 w-4 ml-2 text-[#323956] dark:text-blue-400" title="Stored in Cloud Storage" />
                      )}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">{report.fileSize || 'N/A'}</div>
                    {report.s3Key && (
                      <div className="text-xs text-[#323956] dark:text-blue-400 flex items-center mt-1">
                        <Cloud className="h-3 w-3 mr-1" />
                        Cloud Storage
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-white">
                      {new Date(report.createdAt).toLocaleDateString()}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      by {report.uploadedBy || 'Unknown'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => handleUploadResponse(report)}
                        className="text-[#323956] dark:text-blue-400 hover:text-green-900 dark:hover:text-green-400"
                        title="Upload Response Report"
                      >
                        <Upload className="h-4 w-4" />
                      </button>
                      {user?.role === 'super_admin' ? (
                        <>
                          <button
                            onClick={() => handleViewReport(report)}
                            className="text-[#323956] dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300"
                            title="View Report Details"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDownloadReport(report)}
                            className="text-primary-600 dark:text-blue-400 hover:text-primary-900 dark:hover:text-blue-300"
                            title="Download Report"
                          >
                            <Download className="h-4 w-4" />
                          </button>
                        </>
                      ) : (
                        checkReportLimit(report.clinicId) ? (
                          <button
                            onClick={() => setShowSubscriptionPopup(true)}
                            className="text-orange-600 dark:text-orange-400 hover:text-orange-900 dark:hover:text-orange-300"
                            title="Upgrade required to download"
                          >
                            <Lock className="h-4 w-4" />
                          </button>
                        ) : (
                          <button
                            onClick={() => handleDownloadReport(report)}
                            className="text-primary-600 dark:text-blue-400 hover:text-primary-900 dark:hover:text-blue-300"
                            title="Download"
                          >
                            <Download className="h-4 w-4" />
                          </button>
                        )
                      )}
                      <button
                        onClick={() => handleDeleteReport(report.id)}
                        className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredReports.length === 0 && (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 mb-4">
                {searchTerm || selectedClinic || selectedSupervisor 
                  ? 'No reports match your filters' 
                  : 'No reports uploaded yet'
                }
              </p>
              <button
                onClick={() => setShowUploadModal(true)}
                className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg font-medium"
              >
                Upload First Report
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <ErrorBoundary>
          <UploadReportModal
            onSubmit={handleUploadReport}
            onClose={() => {
              setShowUploadModal(false);
              setSelectedFile(null);
              setUploadProgress(0);
            }}
            clinics={clinics || []}
            patients={supervisors || []}
            register={register}
            handleSubmit={handleSubmit}
            reset={reset}
            watch={watch}
            errors={errors || {}}
            selectedFile={selectedFile}
            onFileSelect={handleFileSelect}
            uploadingFile={uploadingFile}
            uploadProgress={uploadProgress}
          />
        </ErrorBoundary>
      )}

      {/* Response Upload Modal */}
      {showResponseUploadModal && selectedReportForResponse && (
        <ErrorBoundary>
          <ResponseUploadModal
            onSubmit={handleSubmitResponse}
            onClose={() => {
              setShowResponseUploadModal(false);
              setSelectedReportForResponse(null);
              setSelectedFile(null);
              setUploadProgress(0);
            }}
            selectedFile={selectedFile}
            onFileSelect={handleFileSelect}
            uploadingFile={uploadingFile}
            uploadProgress={uploadProgress}
            originalReport={selectedReportForResponse}
          />
        </ErrorBoundary>
      )}

      {/* Report View Modal for Super Admin */}
      {showViewModal && selectedReportForView && (
        <ReportViewModal
          report={selectedReportForView}
          onClose={() => {
            setShowViewModal(false);
            setSelectedReportForView(null);
          }}
        />
      )}

      {/* Response Reports Section - Separate from regular reports */}
      {responseReports.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden mt-6">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-green-50 dark:bg-green-900/20">
            <h3 className="text-lg font-medium text-green-900 dark:text-green-100">
              Response Reports Sent to Project Areas & Supervisors ({responseReports.length})
            </h3>
            <p className="text-sm text-green-700 dark:text-green-300 mt-1">
              These response reports have been sent to project areas and are visible to supervisors
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Response Report
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Patient
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Clinic
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    File Info
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Uploaded
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {responseReports.map((report) => (
                  <tr key={report.id} className="hover:bg-green-50 dark:hover:bg-green-900/10">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-10 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                          <FileText className="h-5 w-5 text-green-600 dark:text-green-400" />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">{report.fileName}</div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">{report.title || 'Response Report'}</div>
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400 mt-1">
                            Response Report
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white">{report.patientName}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white">{report.clinicName}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white flex items-center">
                        {report.fileType || 'PDF'}
                        {report.storedInCloud && (
                          <Cloud className="h-4 w-4 ml-2 text-[#323956] dark:text-blue-400" title="Stored in Cloud Storage" />
                        )}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">{report.fileSize || 'N/A'}</div>
                      {report.s3Key && (
                        <div className="text-xs text-[#323956] dark:text-blue-400 flex items-center mt-1">
                          <Cloud className="h-3 w-3 mr-1" />
                          Cloud Storage
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white">
                        {new Date(report.createdAt).toLocaleDateString()}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        by {report.uploadedBy || 'Super Admin'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => handleViewReport(report)}
                          className="text-[#323956] dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300"
                          title="View Report Details"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDownloadReport(report)}
                          className="text-primary-600 dark:text-blue-400 hover:text-primary-900 dark:hover:text-blue-300"
                          title="Download Report"
                        >
                          <Download className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteReport(report.id)}
                          className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Subscription Popup - Only show for non-Super Admin users */}
      {user?.role !== 'super_admin' && (
        <SubscriptionPopup
          isOpen={showSubscriptionPopup}
          onClose={() => setShowSubscriptionPopup(false)}
          clinicId={superAdminSelectedClinic}
          currentUsage={clinicUsage[superAdminSelectedClinic] || 0}
          onSubscribe={handleSubscription}
          clinicInfo={{
            name: user?.clinicName || user?.name || 'Super Admin',
            email: user?.email || 'admin@aims.com',
            phone: user?.phone || ''
          }}
        />
      )}
    </div>
  );
};

// Response Upload Modal Component
const ResponseUploadModal = ({ 
  onSubmit, 
  onClose, 
  selectedFile,
  onFileSelect,
  uploadingFile,
  uploadProgress,
  originalReport
}) => {
  const [notes, setNotes] = useState('');

  // Safety check for originalReport
  if (!originalReport) {
    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
        <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-red-600">Error</h3>
            <button onClick={onClose || (() => {})} className="text-gray-400 hover:text-gray-600">
              <X className="h-6 w-6" />
            </button>
          </div>
          <p className="text-gray-700 mb-4">Original report data is missing. Please try again.</p>
          <button onClick={onClose || (() => {})} className="bg-red-600 text-white px-4 py-2 rounded">Close</button>
        </div>
      </div>
    );
  }

  const handleSubmit = (e) => {
    e.preventDefault();
    if (onSubmit) {
      onSubmit({ notes });
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Upload Response Report</h3>
          <button
            onClick={onClose || (() => {})}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Original Report Info */}
        <div className="bg-[#E4EFFF] border border-blue-200 rounded-lg p-4 mb-4">
          <h4 className="text-sm font-medium text-blue-900 mb-2">Original Report</h4>
          <div className="space-y-1 text-sm text-blue-700">
            <p><strong>Supervisor:</strong> {originalReport.patientName || 'Unknown Supervisor'}</p>
            <p><strong>Project Area:</strong> {originalReport.clinicName || 'Unknown Project Area'}</p>
            <p><strong>Report:</strong> {originalReport.fileName || 'Unknown Report'}</p>
            <p><strong>Date:</strong> {originalReport.createdAt ? new Date(originalReport.createdAt).toLocaleDateString() : 'Unknown Date'}</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
              <UploadCloud className="h-4 w-4 mr-2 text-[#323956]" />
              Upload Response File *
            </label>
            <div className={`mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-md transition-colors ${
              selectedFile ? 'border-green-300 bg-green-50' : 'border-gray-300'
            }`}>
              <div className="space-y-1 text-center">
                {selectedFile ? (
                  <>
                    <Cloud className="mx-auto h-12 w-12 text-[#323956]" />
                    <div className="text-sm text-[#323956]">
                      <p className="font-medium">{selectedFile.name || 'Unknown file'}</p>
                      <p className="text-xs text-gray-500">
                        {((selectedFile.size || 0) / (1024 * 1024)).toFixed(2)} MB • {selectedFile.type || 'Unknown type'}
                      </p>
                    </div>
                    {uploadingFile && (
                      <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                        <div 
                          className="bg-[#323956] h-2 rounded-full transition-all duration-300" 
                          style={{ width: `${uploadProgress}%` }}
                        ></div>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <Upload className="mx-auto h-12 w-12 text-gray-400" />
                    <div className="flex text-sm text-gray-600">
                      <label htmlFor="response-file-upload" className="relative cursor-pointer bg-white rounded-md font-medium text-[#323956] hover:text-[#323956]">
                        <span>Upload Response File</span>
                        <input
                          id="response-file-upload"
                          name="response-file-upload"
                          type="file"
                          className="sr-only"
                          accept=".pdf,.jpeg,.jpg,.png,.doc,.docx,.edf,.csv,.txt,.xml,.json,.xlsx,.xls,.dcm"
                          onChange={onFileSelect || (() => {})}
                        />
                      </label>
                      <p className="pl-1">or drag and drop</p>
                    </div>
                  </>
                )}
                <p className="text-xs text-gray-500">
                  PDF, JPEG, PNG, DOC, DOCX, EDF, CSV, TXT, XML, JSON, XLSX, DICOM up to 50MB
                  {selectedFile && (
                    <button
                      type="button"
                      onClick={() => onFileSelect && onFileSelect({ target: { files: [] } })}
                      className="ml-2 text-red-500 hover:text-red-700"
                    >
                      Remove
                    </button>
                  )}
                </p>
              </div>
            </div>
            {!selectedFile && <p className="text-red-500 text-xs mt-1">Please select a response file to upload</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Response Notes
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows="3"
              placeholder="Add notes about this response report..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-green-500 focus:border-green-500"
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose || (() => {})}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={uploadingFile || !selectedFile}
              className="px-4 py-2 bg-[#323956] border border-transparent rounded-md text-sm font-medium text-white hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center"
            >
              {uploadingFile ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Uploading Response... ({uploadProgress}%)
                </>
              ) : (
                <>
                  <UploadCloud className="h-4 w-4 mr-2" />
                  Upload Response
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Upload Report Modal Component
const UploadReportModal = ({ 
  onSubmit, 
  onClose, 
  clinics, 
  supervisors,
  register, 
  handleSubmit, 
  reset,
  watch,
  errors,
  selectedFile,
  onFileSelect,
  uploadingFile,
  uploadProgress
}) => {
  // Safety check to prevent crashes
  if (!register || !handleSubmit || !watch) {
    console.error('ERROR: UploadReportModal: Missing required form props');
    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-md">
          <h3 className="text-lg font-bold text-red-600 mb-4">Form Error</h3>
          <p className="text-gray-700 mb-4">Unable to load upload form. Please refresh the page and try again.</p>
          <button onClick={onClose || (() => {})} className="bg-red-600 text-white px-4 py-2 rounded">Close</button>
        </div>
      </div>
    );
  }
  const [availablePatients, setAvailablePatients] = useState([]);
  const watchedClinic = watch('clinicId');

  useEffect(() => {
    const loadPatients = async () => {
      if (watchedClinic) {
        try {
          const clinicSupervisors = await DatabaseService.getSupervisorsByClinic(watchedClinic);
          setAvailablePatients(clinicSupervisors || []);
          console.log('PROJECT AREA: Loaded supervisors for project area:', watchedClinic, 'Count:', clinicSupervisors?.length || 0);
        } catch (error) {
          console.error('ERROR: Error loading supervisors for project area:', error);
          setAvailablePatients([]);
        }
      } else {
        setAvailablePatients([]);
      }
    };
    
    loadPatients();
  }, [watchedClinic]);

  const handleFormSubmit = (data) => {
    try {
      console.log('START: Admin form submitted with data:', data);
      console.log('Form validation errors:', Object.keys(errors).length > 0 ? errors : 'No errors');
      console.log('Available supervisors count:', availablePatients.length);
      console.log('Selected file:', selectedFile ? {
        name: selectedFile.name,
        size: selectedFile.size,
        type: selectedFile.type
      } : 'No file selected');
      
      onSubmit(data);
      reset();
    } catch (error) {
      console.error('ERROR: Form submission error:', error);
      toast.error(`Form submission failed: ${error.message}`);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Upload Supervisor Report</h3>
          <button
            onClick={onClose || (() => {})}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Select Project Area *
            </label>
            <select
              {...register('clinicId', { required: 'Please select a project area' })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">Choose a project area...</option>
              {clinics.map(clinic => (
                <option key={clinic.id} value={clinic.id}>{clinic.name}</option>
              ))}
            </select>
            {errors.clinicId && <p className="text-red-500 text-xs mt-1">{errors.clinicId.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Select Supervisor *
            </label>
            <select
              {...register('supervisorId', { required: 'Please select a supervisor' })}
              disabled={!watchedClinic}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100"
            >
              <option value="">
                {!watchedClinic ? 'Select project area first...' : 'Choose a supervisor...'}
              </option>
              {(availablePatients || []).map(supervisor => (
                <option key={supervisor?.id || Math.random()} value={supervisor?.id || ''}>{supervisor?.name || 'Unnamed Supervisor'}</option>
              ))}
            </select>
            {errors.supervisorId && <p className="text-red-500 text-xs mt-1">{errors.supervisorId.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Report Title
            </label>
            <input
              type="text"
              {...register('title', { required: 'Please enter a report title' })}
              placeholder={selectedFile ? selectedFile.name.replace(/\.[^/.]+$/, "") : "e.g., EEG Analysis Report"}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            />
            {errors.title && <p className="text-red-500 text-xs mt-1">{errors.title.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
              <UploadCloud className="h-4 w-4 mr-2 text-[#323956]" />
              Upload to Cloud Storage *
            </label>
            <div className={`mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-md transition-colors ${
              selectedFile ? 'border-green-300 bg-green-50' : 'border-gray-300'
            }`}>
              <div className="space-y-1 text-center">
                {selectedFile ? (
                  <>
                    <Cloud className="mx-auto h-12 w-12 text-[#323956]" />
                    <div className="text-sm text-[#323956]">
                      <p className="font-medium">{selectedFile.name || 'Unknown file'}</p>
                      <p className="text-xs text-gray-500">
                        {((selectedFile.size || 0) / (1024 * 1024)).toFixed(2)} MB • {selectedFile.type || 'Unknown type'}
                      </p>
                    </div>
                    {uploadingFile && (
                      <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                        <div 
                          className="bg-[#323956] h-2 rounded-full transition-all duration-300" 
                          style={{ width: `${uploadProgress}%` }}
                        ></div>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <Upload className="mx-auto h-12 w-12 text-gray-400" />
                    <div className="flex text-sm text-gray-600">
                      <label htmlFor="file-upload" className="relative cursor-pointer bg-white rounded-md font-medium text-primary-600 hover:text-primary-500">
                        <span>Upload to S3</span>
                        <input
                          id="file-upload"
                          name="file-upload"
                          type="file"
                          className="sr-only"
                          accept=".pdf,.jpeg,.jpg,.png,.edf,.csv,.txt,.xml,.json,.doc,.docx,.xlsx,.xls,.dcm"
                          onChange={onFileSelect}
                        />
                      </label>
                      <p className="pl-1">or drag and drop</p>
                    </div>
                  </>
                )}
                <p className="text-xs text-gray-500">
                  PDF, JPEG, PNG, EDF, CSV, TXT, XML, JSON, DOC, DOCX, XLSX, DICOM up to 50MB
                  {selectedFile && (
                    <button
                      type="button"
                      onClick={() => onFileSelect({ target: { files: [] } })}
                      className="ml-2 text-red-500 hover:text-red-700"
                    >
                      Remove
                    </button>
                  )}
                </p>
              </div>
            </div>
            {!selectedFile && <p className="text-red-500 text-xs mt-1">Please select a file to upload to Cloud Storage</p>}
            
            {/* Cloud Storage Status Indicator */}
            <div className="mt-2 text-xs text-gray-500 flex items-center">
              <Cloud className="h-3 w-3 mr-1" />
              Files will be securely stored in Cloud Storage with encryption
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              {...register('notes')}
              rows="3"
              placeholder="Additional notes about this report..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose || (() => {})}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={uploadingFile || !selectedFile}
              className="px-4 py-2 bg-primary-600 border border-transparent rounded-md text-sm font-medium text-white hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center"
            >
              {uploadingFile ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Uploading to S3... ({uploadProgress}%)
                </>
              ) : (
                <>
                  <UploadCloud className="h-4 w-4 mr-2" />
                  Upload to Cloud Storage
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Report View Modal Component for Super Admin
const ReportViewModal = ({ report, onClose }) => {
  const [reportContent, setReportContent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
     const [isFullView, setIsFullView] = useState(false);
   const [showPdfPreview, setShowPdfPreview] = useState(true); // Default to true to show PDF immediately

  useEffect(() => {
    if (report) {
      loadReportContent();
    }
  }, [report]);

  // Safety check - don't render if report is null
  if (!report) {
    return null;
  }

  const loadReportContent = async () => {
    if (!report) return;
    
    setLoading(true);
    setError(null);
    
    try {
      let content = null;
      let contentSource = 'not found';
      
      console.log('DEBUG: Loading report content for:', report.fileName);
      console.log('INFO: Report data:', {
        id: report.id,
        s3Key: report.s3Key,
        fileUrl: report.fileUrl,
        storedInCloud: report.storedInCloud
      });
      
      // Method 1: Try Supabase Storage with file_path
      const filePath = report.file_path || report.filePath || report.storagePath || report.s3Key;

      if (filePath) {
        try {
          console.log('DEBUG: Trying Supabase Storage with path:', filePath);

          // Import StorageService
          const { default: StorageService } = await import('../../services/storageService');

          // Get signed URL for the file
          const fileUrl = await StorageService.getSignedUrl(filePath, 3600); // 1 hour expiry

          if (fileUrl) {
            console.log('SUCCESS: Got signed URL from Supabase Storage:', fileUrl);
            // For PDF files, we can embed them directly
            if (report.fileName?.toLowerCase().endsWith('.pdf')) {
              content = fileUrl; // Store the URL to display PDF
              contentSource = 'Supabase Storage PDF';
            } else {
              content = `URL Reference: ${fileUrl}`;
              contentSource = 'Supabase Storage';
            }
          }
        } catch (storageError) {
          console.warn('WARNING: Error fetching from Supabase Storage:', storageError);
          console.error('Storage error details:', storageError);
        }
      } else {
        console.warn('WARNING: No file path found in report data');
      }

      // Method 2: Try file URL if available
      if (!content && report.fileUrl) {
        console.log('DEBUG: Using file URL:', report.fileUrl);
        try {
          content = `URL Reference: ${report.fileUrl}`;
          contentSource = 'File URL';
          console.log('SUCCESS: Using file URL as reference');
        } catch (urlError) {
          console.warn('WARNING: Error processing file URL:', urlError);
        }
      }

      // Method 3: Create informative message if no content found
      if (!content) {
        console.log('DEBUG: No actual content found, creating informative message');
        content = `No Content Available

Report: ${report.fileName || 'Unknown'}
Supervisor: ${report.patientName || 'Unknown Supervisor'}
Project Area: ${report.clinicName || 'Unknown Project Area'}
Upload Date: ${report.createdAt ? new Date(report.createdAt).toLocaleDateString() : 'Unknown'}

Storage Information:
- S3 Key: ${report.s3Key || 'None'}
- File URL: ${report.fileUrl || 'None'}
- Storage Type: ${report.storedInCloud ? 'Cloud (Cloud Storage)' : 'Local'}
- File Size: ${report.fileSize || 'Unknown'}
- File Type: ${report.fileType || 'Unknown'}

Status: The report metadata exists but the actual file content could not be found.
This may happen if:
1. The file was not properly uploaded
2. The file was stored externally and is no longer accessible
3. There was an error during the upload process

Recommendation: Contact the clinic to re-upload this report.`;
        contentSource = 'No Content Message';
      }
      
      console.log('FILE: Content loaded from:', contentSource);
      console.log('DATA: Content type:', typeof content, 'Length:', content?.length || 0);
      
      setReportContent(content);
      
      if (!content) {
        setError('Report content not found. File may have been moved or deleted.');
      }
    } catch (error) {
      console.error('ERROR: Error loading report content:', error);
      setError('Failed to load report content: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleFullView = () => {
    setIsFullView(!isFullView);
  };

  return (
         <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
       <div className={`relative mx-auto border shadow-lg rounded-md bg-white ${
         isFullView 
           ? 'top-1 left-1 right-1 bottom-1 max-w-none h-[calc(100vh-0.5rem)] p-2' 
           : 'top-10 max-w-5xl p-5'
       }`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <FileText className="h-5 w-5 mr-2 text-[#323956]" />
            Report Details - {report?.fileName || 'Unknown Report'}
          </h3>
          <div className="flex items-center space-x-2">
            {reportContent && (
              <button
                onClick={toggleFullView}
                className="text-[#323956] hover:text-blue-800 p-2 rounded-lg hover:bg-[#E4EFFF]"
                title={isFullView ? 'Exit Full View' : 'Full View'}
              >
                {isFullView ? (
                  <X className="h-5 w-5" />
                ) : (
                  <Eye className="h-5 w-5" />
                )}
              </button>
            )}
            <button
              onClick={onClose || (() => {})}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Show details only in normal view */}
        {!isFullView && (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              {/* Report Information */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="text-md font-semibold text-gray-900 mb-3 flex items-center">
                  <FileText className="h-4 w-4 mr-2" />
                  Report Information
                </h4>
                <div className="space-y-2 text-sm">
                  <div><span className="font-medium">File Name:</span> {report?.fileName || 'N/A'}</div>
                  <div><span className="font-medium">Title:</span> {report?.title || 'N/A'}</div>
                  <div><span className="font-medium">Type:</span> {report?.reportType || 'EEG'}</div>
                  <div><span className="font-medium">Size:</span> {report?.fileSize || 'N/A'}</div>
                  <div><span className="font-medium">Uploaded:</span> {report?.createdAt ? new Date(report.createdAt).toLocaleString() : 'N/A'}</div>
                  <div><span className="font-medium">Uploaded by:</span> {report?.uploadedBy || 'N/A'}</div>
                </div>
              </div>

              {/* Supervisor & Project Area Information */}
              <div className="bg-[#E4EFFF] p-4 rounded-lg">
                <h4 className="text-md font-semibold text-blue-900 mb-3 flex items-center">
                  <Users className="h-4 w-4 mr-2" />
                  Supervisor & Project Area Details
                </h4>
                <div className="space-y-2 text-sm text-blue-800">
                  <div><span className="font-medium">Supervisor:</span> {report?.patientName || 'Unknown Supervisor'}</div>
                  <div><span className="font-medium">Project Area:</span> {report?.clinicName || 'Unknown Project Area'}</div>
                  <div><span className="font-medium">Supervisor ID:</span> {report?.patientId || 'N/A'}</div>
                  <div><span className="font-medium">Project Area ID:</span> {report?.clinicId || 'N/A'}</div>
                </div>
              </div>
            </div>

            {/* Notes */}
            {report?.notes && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                <h4 className="text-md font-semibold text-yellow-900 mb-2">Notes</h4>
                <p className="text-sm text-yellow-800">{report.notes}</p>
              </div>
            )}
          </>
        )}

        {/* Report Content Preview */}
        <div className={`border rounded-lg p-4 mb-6 ${isFullView ? 'flex-1 flex flex-col' : ''}`}>
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-md font-semibold text-gray-900">
              {isFullView ? 'Full Report View' : 'Report Preview'}
            </h4>
            {reportContent && !isFullView && (
              <button
                onClick={toggleFullView}
                className="bg-[#323956] hover:bg-[#232D3C] text-white px-4 py-2 rounded-md flex items-center text-sm"
              >
                <Eye className="h-4 w-4 mr-2" />
                View Full Size
              </button>
            )}
          </div>

          <div className={`bg-gray-100 rounded-lg p-6 ${isFullView ? 'flex-1 flex flex-col' : ''}`}>
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Loading report content...</p>
              </div>
            ) : error ? (
              <div className="text-center py-8">
                <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                <h5 className="text-lg font-medium text-red-900 mb-2">Unable to Load Report</h5>
                <p className="text-red-700 mb-4">{error}</p>
                <button
                  onClick={loadReportContent}
                  className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md"
                >
                  Retry
                </button>
              </div>
            ) : reportContent ? (
              <div className={`${isFullView ? 'flex-1 flex flex-col' : 'text-center py-8'}`}>
                {!isFullView && (
                  <>
                    <FileText className="h-16 w-16 text-[#323956] mx-auto mb-4" />
                    <h5 className="text-lg font-medium text-green-900 mb-2">Report Ready</h5>
                    <p className="text-green-700 mb-4">
                      Report content is available. Click "View Full Size" to see the complete document.
                    </p>
                    <div className="text-xs text-gray-500 bg-white p-2 rounded border max-w-md mx-auto mb-4">
                      <strong>Technical Info:</strong><br/>
                      Storage: {report?.s3Key ? 'Cloud Storage' : 'Local Storage'}<br/>
                      Content Type: {report?.fileType || 'application/pdf'}<br/>
                      Content Size: {reportContent?.length || 0} characters<br/>
                      Format: {reportContent?.startsWith('data:') ? 'Base64 Data' : reportContent?.startsWith('http') ? 'URL' : 'Raw Data'}
                    </div>
                  </>
                )}
                
                                 {/* Report Content Display */}
                 <div className={`${isFullView ? 'flex-1 flex flex-col' : 'mt-4'}`}>
                   {/* Hide header controls when showing PDF in full view */}
                   {!isFullView && (
                     <div className="flex items-center justify-between mb-2">
                       <h6 className="text-sm font-medium text-gray-900">Report Content</h6>
                       <div className="flex items-center space-x-3">
                         {/* PDF Preview Toggle */}
                         {(reportContent?.startsWith('data:application/pdf') || reportContent?.startsWith('http')) && (
                           <div className="flex items-center space-x-1">
                             <span className="text-xs text-gray-500">View:</span>
                             <button
                               onClick={() => setShowPdfPreview(!showPdfPreview)}
                               className={`text-xs px-2 py-0.5 rounded border ${
                                 showPdfPreview ? 'bg-green-100 text-green-700 border-green-300' : 'bg-gray-100 text-gray-700 border-gray-300'
                               }`}
                             >
                               {showPdfPreview ? 'Raw Data' : 'PDF View'}
                             </button>
                           </div>
                         )}

                         {/* Character Count */}
                         <div className="flex space-x-2 text-xs text-gray-500">
                           <span>Characters: {reportContent?.length || 0}</span>
                         </div>
                       </div>
                     </div>
                   )}
                   
                   {/* PDF Viewer */}
                   {(reportContent?.startsWith('data:application/pdf') || reportContent?.startsWith('http')) && showPdfPreview ? (
                     <div className={`border rounded-md bg-white ${
                       isFullView 
                         ? 'flex-1 min-h-[600px]' 
                         : 'h-96 min-h-[400px] max-h-[800px]'
                     }`}>
                       <iframe
                         src={reportContent?.startsWith('http') ? reportContent : `${reportContent}#toolbar=0&navpanes=0&scrollbar=0&statusbar=0&messages=0&scrollbar=0&view=FitH`}
                         className="w-full h-full border-0 rounded-md"
                         title={`PDF Viewer - ${report?.fileName || 'Report'}`}
                         style={{
                           minHeight: isFullView ? '600px' : '400px',
                           height: '100%',
                           width: '100%'
                         }}
                       />
                     </div>
                   ) : (
                     /* Text/Data Display */
                     <textarea
                       value={(() => {
                         if (!reportContent) return 'No content available';
                         
                         // Handle different content types
                         if (reportContent.startsWith('data:application/pdf')) {
                           return `PDF REPORT CONTENT\n\n` +
                             `FILE: File: ${report?.fileName || 'Unknown Report'}\n` +
                             ` Supervisor: ${report?.patientName || 'Unknown Supervisor'}\n` +
                             `PROJECT AREA: Project Area: ${report?.clinicName || 'Unknown Project Area'}\n` +
                             ` Date: ${report?.createdAt ? new Date(report.createdAt).toLocaleDateString() : 'Unknown'}\n` +
                             `DATA: Size: ${reportContent.length} characters (Base64 encoded)\n\n` +
                             `INFO: REPORT TYPE: ${report?.reportType || 'Medical Report'}\n\n` +
                             `═══════════════════════════════════════════════════════\n\n` +
                             `DEBUG: PDF CONTENT PREVIEW:\n\n` +
                             `This is a PDF document containing medical report data.\n` +
                             `The PDF is properly encoded and stored in base64 format.\n\n` +
                             `Click "PDF View" button above to see the actual PDF document.\n\n` +
                             `Base64 Encoded PDF Data (first 200 characters):\n` +
                             `${reportContent.split(',')[1]?.substring(0, 200) || 'No data'}...\n\n` +
                             `═══════════════════════════════════════════════════════\n\n` +
                             `NOTE: FULL BASE64 CONTENT:\n\n` +
                             `${reportContent}`;
                           
                         } else if (reportContent.startsWith('data:')) {
                           try {
                             const [header, content] = reportContent.split(',');
                             const contentType = header.match(/data:([^;]+)/)?.[1] || 'unknown';
                             
                             // Try to decode if it's text-based content
                             if (contentType.includes('text') || contentType.includes('json')) {
                               try {
                                 const decoded = atob(content);
                                 return `DECODED REPORT CONTENT\n\n` +
                                   `FILE: File: ${report?.fileName || 'Unknown'}\n` +
                                   ` Supervisor: ${report?.patientName || 'Unknown Supervisor'}\n` +
                                   `PROJECT AREA: Project Area: ${report?.clinicName || 'Unknown Project Area'}\n` +
                                   `DATA: Content Type: ${contentType}\n` +
                                   ` Size: ${decoded.length} characters\n\n` +
                                   `═══════════════════════════════════════════════════════\n\n` +
                                   `INFO: REPORT CONTENT:\n\n${decoded}`;
                               } catch (decodeError) {
                                 console.warn('Failed to decode base64 content:', decodeError);
                               }
                             }
                             
                             return `ENCODED REPORT DATA\n\n` +
                               `FILE: File: ${report?.fileName || 'Unknown'}\n` +
                               ` Supervisor: ${report?.patientName || 'Unknown Supervisor'}\n` +
                               `PROJECT AREA: Project Area: ${report?.clinicName || 'Unknown Project Area'}\n` +
                               `DATA: Content Type: ${contentType}\n` +
                               ` Encoded Size: ${content?.length || 0} characters\n\n` +
                               `═══════════════════════════════════════════════════════\n\n` +
                               `INFO: BASE64 ENCODED CONTENT:\n\n${content?.substring(0, 2000) || 'No content'}${content?.length > 2000 ? '\n\n... (content truncated, scroll up to see full data)' : ''}`;
                               
                           } catch (dataError) {
                             console.error('Error processing data content:', dataError);
                             return reportContent;
                           }
                         } else if (reportContent.startsWith('http')) {
                           return `URL REFERENCE\n\nFILE: File: ${report?.fileName || 'Unknown'}\n Supervisor: ${report?.patientName || 'Unknown Supervisor'}\nPROJECT AREA: Project Area: ${report?.clinicName || 'Unknown Project Area'}\n URL: ${reportContent}\n\nWARNING:  NOTE: This report is stored as a URL reference.\nThe actual content may be hosted externally.`;
                         } else {
                           // Plain text or other content
                           return `REPORT CONTENT\n\n` +
                             `FILE: File: ${report?.fileName || 'Unknown'}\n` +
                             ` Supervisor: ${report?.patientName || 'Unknown Supervisor'}\n` +
                             `PROJECT AREA: Project Area: ${report?.clinicName || 'Unknown Project Area'}\n` +
                             ` Date: ${report?.createdAt ? new Date(report.createdAt).toLocaleDateString() : 'Unknown'}\n\n` +
                             `═══════════════════════════════════════════════════════\n\n` +
                             `INFO: REPORT DATA:\n\n${reportContent}`;
                         }
                       })()}
                       readOnly
                       className={`w-full border rounded-md p-3 font-mono text-xs bg-white resize-both overflow-auto ${
                         isFullView 
                           ? 'flex-1 min-h-[400px]' 
                           : 'h-64 min-h-[200px] max-h-[500px]'
                       } whitespace-pre-wrap`}
                       style={{
                         minWidth: '100%',
                         maxWidth: '100%',
                         resize: isFullView ? 'vertical' : 'both'
                       }}
                       placeholder="Report content will appear here..."
                     />
                   )}
                   
                   {!isFullView && (<div className="mt-2 flex items-center justify-between text-xs text-gray-500">
                     <div className="flex space-x-4">
                       <span>FILE: Type: {
                         reportContent?.startsWith('data:application/pdf') ? 'PDF Document' :
                         reportContent?.startsWith('data:') ? 'Base64 Data' :
                         reportContent?.startsWith('http') ? 'URL Reference' :
                         'Text Content'
                       }</span>
                       <span>️ Storage: {report?.s3Key ? 'Cloud Storage' : 'Local'}</span>
                     </div>
                     <div>
                       <span>IDEA: Tip: {reportContent?.startsWith('data:application/pdf') ? 'Click "PDF View" to see the actual document' : 'Drag the bottom-right corner to resize'}</span>
                     </div>
                   </div>)}
                 </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h5 className="text-lg font-medium text-gray-900 mb-2">No Preview Available</h5>
                <p className="text-gray-600">
                  Report metadata is available, but the file content could not be loaded.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-between items-center">
          <div>
            <button
              onClick={() => {
                try {
                  console.log(' DEBUG - Report Object:', report);
                  console.log(' DEBUG - Report Content:', reportContent);
                  
                  try {
                    const reportsData = localStorage.getItem('reports');
                    const reports = reportsData ? JSON.parse(reportsData) : [];
                    console.log(' DEBUG - localStorage reports:', reports);
                  } catch (reportsError) {
                    console.log(' DEBUG - localStorage reports ERROR:', reportsError);
                  }
                  
                  try {
                    const s3Data = localStorage.getItem('s3MockFiles');
                    const s3Files = s3Data ? JSON.parse(s3Data) : [];
                    console.log(' DEBUG - localStorage s3MockFiles:', s3Files);
                  } catch (s3Error) {
                    console.log(' DEBUG - localStorage s3MockFiles ERROR:', s3Error);
                  }
                  
                  toast.success('Debug info logged to console (F12 → Console)');
                } catch (error) {
                  console.error(' DEBUG - Error during debug:', error);
                  toast.error('Debug failed - check console for error details');
                }
              }}
              className="px-3 py-1 bg-gray-500 text-white text-xs rounded hover:bg-gray-600"
            >
              Debug Info
            </button>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Close
            </button>
            {reportContent && (
              <button
                onClick={toggleFullView}
                className="px-4 py-2 bg-[#323956] border border-transparent rounded-md text-sm font-medium text-white hover:bg-[#232D3C]"
              >
                {isFullView ? 'Exit Full View' : 'View Full Size'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupervisorReports;